import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Construction, Loader2, Megaphone, Power, Trash2 } from 'lucide-react'
import { adminApi, systemApi } from '@/lib/api'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Callout } from '@/components/ui/callout'
import { useToast } from '@/hooks/use-toast'
import { formatDate } from '@/lib/utils'

// datetime-local <-> ISO helpers: the input works in the browser's local
// time and has no seconds/timezone, ISO is what the API expects.
function toLocalInput(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function toIso(local: string): string | null {
  if (!local) return null
  return new Date(local).toISOString()
}

export default function Maintenance() {
  const { t, i18n } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const { data } = useQuery({
    queryKey: ['system', 'maintenance'],
    queryFn: systemApi.getMaintenance,
  })

  const [message, setMessage] = useState('')
  const [reopenAt, setReopenAt] = useState('')
  const [noticeStart, setNoticeStart] = useState('')
  const [noticeEnd, setNoticeEnd] = useState('')
  const [noticeReason, setNoticeReason] = useState('')
  const [prefilled, setPrefilled] = useState(false)

  const noticeDue = !!data?.notice_start && new Date(data.notice_start) <= new Date()

  // When the announced time arrives, the activation fields present
  // themselves already filled in with what was promised — once per notice,
  // so it doesn't fight typing done afterwards.
  useEffect(() => {
    if (data?.active) return
    if (noticeDue && !prefilled && !message && data?.notice_reason) {
      setMessage(data.notice_reason)
      setReopenAt(toLocalInput(data.notice_end))
      setPrefilled(true)
    }
  }, [noticeDue, prefilled, message, data])

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['system', 'maintenance'] })

  const { mutate: activate, isPending: activating } = useMutation({
    mutationFn: () => adminApi.activateMaintenance({ message: message.trim(), reopen_at: toIso(reopenAt) }),
    onSuccess: () => {
      refresh()
      setPrefilled(false)
      toast({ title: t('maintenance.activated') })
    },
    onError: (e: any) =>
      toast({
        title: e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || 'Errore',
        variant: 'destructive',
      }),
  })

  const { mutate: deactivate, isPending: deactivating } = useMutation({
    mutationFn: () => adminApi.deactivateMaintenance(),
    onSuccess: () => {
      refresh()
      setMessage('')
      setReopenAt('')
      toast({ title: t('maintenance.deactivated') })
    },
    onError: (e: any) => toast({ title: e?.response?.data?.detail || 'Errore', variant: 'destructive' }),
  })

  const { mutate: publishNotice, isPending: publishing } = useMutation({
    mutationFn: () =>
      adminApi.setMaintenanceNotice({
        start: toIso(noticeStart)!,
        end: toIso(noticeEnd)!,
        reason: noticeReason.trim(),
      }),
    onSuccess: () => {
      refresh()
      setNoticeStart('')
      setNoticeEnd('')
      setNoticeReason('')
      toast({ title: t('maintenance.noticePublished') })
    },
    onError: (e: any) =>
      toast({
        title: e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || 'Errore',
        variant: 'destructive',
      }),
  })

  const { mutate: withdrawNotice, isPending: withdrawing } = useMutation({
    mutationFn: () => adminApi.withdrawMaintenanceNotice(),
    onSuccess: () => { refresh(); toast({ title: t('maintenance.noticeWithdrawn') }) },
    onError: (e: any) => toast({ title: e?.response?.data?.detail || 'Errore', variant: 'destructive' }),
  })

  const active = !!data?.active
  const hasNotice = !!data?.notice_reason

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6 stagger">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Construction className="w-6 h-6 text-mim-blue" />
          {t('maintenance.pageTitle')}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">{t('maintenance.pageSubtitle')}</p>
      </div>

      <Card className={active ? 'border-rosso/50' : undefined}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Power className={active ? 'w-4 h-4 text-rosso' : 'w-4 h-4 text-verde'} />
            {active ? t('maintenance.statusActive') : t('maintenance.statusInactive')}
          </CardTitle>
          {active && data?.activated_at && (
            <CardDescription className="text-xs">
              {t('maintenance.activeSince', { date: formatDate(data.activated_at, i18n.language) })}
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {active ? (
            <>
              <div>
                <Label className="text-xs">{t('maintenance.messageLabel')}</Label>
                <p className="text-sm mt-1 whitespace-pre-wrap">{data?.message}</p>
              </div>
              <div>
                <Label className="text-xs">{t('maintenance.reopenLabel')}</Label>
                <p className="text-sm mt-1">
                  {data?.reopen_at ? formatDate(data.reopen_at, i18n.language) : t('maintenance.closureNoReopen')}
                </p>
              </div>
              <Button
                variant="destructive"
                disabled={deactivating}
                onClick={() => deactivate()}
              >
                {deactivating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Power className="w-4 h-4 mr-2" />}
                {t('maintenance.deactivate')}
              </Button>
            </>
          ) : (
            <>
              {noticeDue && (
                <Callout variant="note" title={t('maintenance.noticeTitle')}>
                  {t('maintenance.noticeDue')}
                </Callout>
              )}
              <div>
                <Label className="text-xs" htmlFor="mnt-message">{t('maintenance.messageLabel')}</Label>
                <Textarea
                  id="mnt-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('maintenance.messagePlaceholder')}
                  rows={3}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs" htmlFor="mnt-reopen">{t('maintenance.reopenLabel')}</Label>
                <Input
                  id="mnt-reopen"
                  type="datetime-local"
                  value={reopenAt}
                  onChange={(e) => setReopenAt(e.target.value)}
                  className="mt-1 h-9"
                />
                <p className="text-xs text-muted-foreground mt-1">{t('maintenance.reopenHint')}</p>
              </div>
              <Button
                disabled={activating || message.trim().length < 5}
                onClick={() => activate()}
              >
                {activating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Power className="w-4 h-4 mr-2" />}
                {t('maintenance.activate')}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Megaphone className="w-4 h-4 text-mim-blue" />
            {t('maintenance.noticeTitle')}
          </CardTitle>
          <CardDescription className="text-xs">{t('maintenance.noticeSubtitle')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {hasNotice ? (
            <>
              <Callout variant="note">
                <p className="font-medium">{t('maintenance.noticeCurrent')}</p>
                <p className="mt-1">
                  {formatDate(data!.notice_start!, i18n.language)} → {formatDate(data!.notice_end!, i18n.language)}
                </p>
                <p className="mt-1">{data?.notice_reason}</p>
              </Callout>
              <p className="text-xs text-muted-foreground">{t('maintenance.noticeWithdrawHint')}</p>
              <Button
                variant="outline"
                className="text-destructive"
                disabled={withdrawing}
                onClick={() => withdrawNotice()}
              >
                {withdrawing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                {t('maintenance.noticeWithdraw')}
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">{t('maintenance.noticeNone')}</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">{t('maintenance.noticeStart')}</Label>
                  <Input
                    type="datetime-local"
                    className="mt-1 h-9"
                    value={noticeStart}
                    onChange={(e) => setNoticeStart(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-xs">{t('maintenance.noticeEnd')}</Label>
                  <Input
                    type="datetime-local"
                    className="mt-1 h-9"
                    value={noticeEnd}
                    onChange={(e) => setNoticeEnd(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">{t('maintenance.noticeReason')}</Label>
                <Textarea
                  value={noticeReason}
                  onChange={(e) => setNoticeReason(e.target.value)}
                  placeholder={t('maintenance.noticeReasonPlaceholder')}
                  rows={2}
                  className="mt-1"
                />
              </div>
              <Button
                variant="outline"
                disabled={publishing || !noticeStart || !noticeEnd || noticeReason.trim().length < 5}
                onClick={() => publishNotice()}
              >
                {publishing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Megaphone className="w-4 h-4 mr-2" />}
                {t('maintenance.noticePublish')}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
